import { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import PersonIcon from '@mui/icons-material/Person';
import LogoutIcon from '@mui/icons-material/Logout';
import SettingsIcon from '@mui/icons-material/Settings';
import { useAppDispatch } from '@/hooks/useAppDispatch';
import { useAppSelector } from '@/hooks/useAppSelector';
import { logout } from '@/store/authSlice';
import { useActiveModule } from '@/hooks/useActiveModule';
import { moduleAdminPath } from '@/config/moduleDefaults';
import { resolveLogoutLoginPath } from '@/routes/ModuleScopeGuard';
import { cn } from '@/utils/cn';

export function ProfileMenu() {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const user = useAppSelector((s) => s.auth.user);
  const entryModule = useAppSelector((s) => s.auth.entryModule);
  const activeModule = useActiveModule();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const close = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const handleLogout = async () => {
    const loginPath = resolveLogoutLoginPath({
      masterAdmin: user?.masterAdmin,
      activeModule,
      entryModule,
      pathname: location.pathname,
    });
    await dispatch(logout());
    navigate(loginPath, { replace: true });
  };

  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        className="workspace-header-icon-btn"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="true"
        aria-label="Account menu"
      >
        <PersonIcon fontSize="small" />
      </button>
      {open && (
        <div
          className="absolute right-0 top-full z-50 mt-2 w-56 rounded-xl border border-slate-200 bg-white py-1 shadow-panel dark:border-slate-700 dark:bg-slate-900"
          role="menu"
        >
          <div className="border-b border-slate-100 px-3 py-2 dark:border-slate-800">
            <p className="truncate text-sm font-medium text-slate-900 dark:text-slate-100">
              {user?.firstName} {user?.lastName}
            </p>
            <p className="truncate text-xs text-slate-500">{user?.email}</p>
          </div>
          <button
            type="button"
            role="menuitem"
            className={cn('flex w-full items-center gap-2 px-3 py-2 text-sm text-slate-900 hover:bg-slate-50 dark:text-white dark:hover:bg-slate-800')}
            onClick={() => {
              setOpen(false);
              navigate(
                moduleAdminPath(activeModule ?? entryModule ?? 'POS', 'profile'),
              );
            }}
          >
            <SettingsIcon fontSize="small" />
            Organization profile
          </button>
          <button
            type="button"
            role="menuitem"
            className="flex w-full items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-950"
            onClick={() => void handleLogout()}
          >
            <LogoutIcon fontSize="small" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
